import React from 'react';
import { Button, Tag } from 'antd';
import { Participant } from './TeacherOlympiadList';
import SecondTourResultPopover from './SecondTourResultPopover';
import SecondTourStatusPopover from './SecondTourStatusPopover';
import FirstTourAnswersPopover from './FirstTourAnswersPopover';
import {DownloadOutlined} from '@ant-design/icons';
import { downloadFileWithParams } from '../../../../api/httpRequest';

export const getScoreCellStyle = (score: string, passingScore: number) => {
    const numericScore = parseInt(score || '0');
    if (passingScore > 0) {
        return {
            backgroundColor: numericScore >= passingScore ? '#f6ffed' : '#fff2f0',
            color: numericScore >= passingScore ? '#389e0d' : '#cf1322',
            fontWeight: 'bold' as const
        };
    }
    return {};
};

const SecondTourStatusDisplay: React.FC<{ status: string }> = ({ status }) => {

    switch (status) {
        case 'participant':
            return <Tag color="blue">Участник</Tag>;
        case 'prizewinner':
            return <Tag color="orange">Призер</Tag>;
        case 'winner':
            return <Tag color="green">Победитель</Tag>;
        default:
            return <Tag>Не указан</Tag>;
    }
};

const handleDownload = (filename: string, participant: any, olympiadId: number) => {
    downloadFileWithParams("/server/olympiad/files", {
        fileName: filename, 
        participant: participant, 
        olympiadId: olympiadId
    })
    .then(response => {
        const href = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', `${filename}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
    });
};

export const getParticipantsTableColumns = (
    participants: Participant[],
    passingScore: number, 
    olympiadId: number, 
    maxScore: number,
    onResultUpdate?: () => void,
    isCurator?: boolean,
) => {
    const genderFilters = [
        { value: '1', text: 'мужской' },
        { value: '0', text: 'женский' }
    ];

    const firstTourPassFilters = [
        { value: 'true', text: 'Допущен' },
        { value: 'false', text: 'Не допущен' },
        { value: 'null', text: 'Нет решения' }
    ];

    const secondTourStatusFilters = [
        { value: 'participant', text: 'Участник' },
        { value: 'prizewinner', text: 'Призер' },
        { value: 'winner', text: 'Победитель' }
    ];

    const uniqueValues = (key: keyof Participant) => {
        const values = participants.map(p => p[key]).filter(Boolean);
        return Array.from(new Set(values)).map(value => ({
            value: value as string,
            text: value as string
        }));
    };

    const columns: any[] = [
        {
            title: '',
            key: 'selection',
            width: 20,
            fixed: 'left' as const,
        },
        {
            title: 'Номер участника',
            key: 'id',
            dataIndex: 'id',
            width: 100,
            fixed: 'left' as const,
            sorter: (a: Participant, b: Participant) => a.id - b.id,
            filterSearch: true,
            filters: uniqueValues('id').map(item => ({ 
                ...item, 
                value: item.value.toString() 
            })),
            onFilter: (value: any, record: Participant) => 
                record.id.toString().toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'ФИО',
            dataIndex: 'fio',
            key: 'fio',
            width: 200,
            fixed: 'left' as const,
            sorter: (a: Participant, b: Participant) => a.fio.localeCompare(b.fio),
            filterSearch: true,
            filters: uniqueValues('fio'),
            onFilter: (value: any, record: Participant) => 
                record.fio.toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'Пол',
            dataIndex: 'gender',
            key: 'gender',
            width: 80,
            render: (text: any) => text === "1" ? "мужской" : "женский",
            sorter: (a: Participant, b: Participant) => a.gender.localeCompare(b.gender),
            filters: genderFilters,
            filterSearch: true,
            onFilter: (value: any, record: Participant) => record.gender === value,
        },
        {
            title: 'Дата рождения',
            dataIndex: 'birthDate',
            key: 'birthDate',
            width: 120,
            sorter: (a: Participant, b: Participant) =>
                new Date(a.birthDate).getTime() - new Date(b.birthDate).getTime(),
            filterSearch: true,
            filters: uniqueValues('birthDate'),
            onFilter: (value: any, record: Participant) => 
                record.birthDate.toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'Первый тур',
            dataIndex: 'firstTourOption1',
            key: 'firstTourOption1',
            width: 150,
            render: (score: string, record: Participant) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={getScoreCellStyle(score, passingScore)}>
                        {score}
                    </div>
                    {record.firstTourAnswers && record.firstTourAnswers.length > 0 && (
                        <FirstTourAnswersPopover
                            participant={record}
                            answers={record.firstTourAnswers}
                        />
                    )}
                </div>
            ),
            sorter: (a: Participant, b: Participant) => 
                parseInt(a.firstTourOption1 || '0') - parseInt(b.firstTourOption1 || '0'),
            filterSearch: true,
            filters: uniqueValues('firstTourOption1'),
            onFilter: (value: any, record: Participant) => 
                record.firstTourOption1.toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'Допуск',
            dataIndex: 'firstTourPass',
            key: 'firstTourPass',
            width: 120,
            render: (firstTourPass: boolean | null) => {
                let color, status;
                
                if (firstTourPass === null) {
                    color = "default";
                    status = "Нет решения";
                } else if (firstTourPass) {
                    color = 'green';
                    status = 'Допущен';
                } else {
                    color = 'red';
                    status = 'Не допущен';
                }
                
                return <Tag color={color}>{status}</Tag>;
            },
            filters: firstTourPassFilters,
            filterSearch: true,
            onFilter: (value: any, record: Participant) => {
                if (value === 'null') return record.firstTourPass === null;
                return record.firstTourPass === (value === 'true');
            },
        },
        {
            title: 'Второй тур',
            dataIndex: 'secondTourResult',
            key: 'secondTourResult',
            width: 200,
            render: (result: string, record: Participant) => (
                isCurator ? (
                    result || 'Не участвовал'
                ) : (
                    <SecondTourResultPopover
                        participant={record}
                        currentResult={result}
                        passingScore={passingScore}
                        maxScore={maxScore}
                        olympiadId={olympiadId}
                        onResultUpdate={onResultUpdate}
                    />
                )
            ),
            filterSearch: true,
            filters: uniqueValues('secondTourResult'),
            onFilter: (value: any, record: Participant) => 
                record.secondTourResult.toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'Статус во втором туре',
            key: 'secondTourStatus',
            width: 200,
            render: (record: Participant) => (
                isCurator ? (
                    <SecondTourStatusDisplay status={record.secondTourStatus} />
                ) : (
                    <SecondTourStatusPopover
                        participant={record}
                        olympiadId={olympiadId}
                        onStatusUpdate={onResultUpdate}
                    />
                )
            ),
            filters: secondTourStatusFilters,
            filterSearch: true,
            onFilter: (value: any, record: Participant) => record.secondTourStatus === value,
        },
        {
            title: 'Контакты',
            children: [
                {
                    title: 'Электронный адрес',
                    dataIndex: 'email',
                    key: 'email',
                    width: 200,
                    filterSearch: true,
                    filters: uniqueValues('email'),
                    onFilter: (value: any, record: Participant) => 
                        record.email.toLowerCase().includes(value.toLowerCase()),
                },
                {
                    title: 'Телефон',
                    dataIndex: 'phone',
                    key: 'phone',
                    width: 130,
                    filterSearch: true,
                    filters: uniqueValues('phone'),
                    onFilter: (value: any, record: Participant) => 
                        record.phone.toLowerCase().includes(value.toLowerCase()),
                },
            ],
        },
        {
            title: 'Место проведения',
            children: [
                {
                    title: 'Город',
                    dataIndex: 'city',
                    key: 'city',
                    width: 120,
                    filterSearch: true,
                    filters: uniqueValues('city'),
                    onFilter: (value: any, record: Participant) => 
                        record.city.toLowerCase().includes(value.toLowerCase()),
                },
                {
                    title: 'Площадка',
                    dataIndex: 'placeName',
                    key: 'placeName',
                    width: 200,
                    render: (placeName: string, record: Participant) => (
                        <div>
                            <Tag color="blue" 
                                style={{
                                    marginTop: 4,
                                    whiteSpace: 'normal', // Разрешает перенос строк
                                    wordBreak: 'break-word', // Принудительный перенос длинных слов
                                    maxWidth: '100%', // Ограничивает ширину
                                }}>
                                {record.placeName}
                            </Tag>
                        </div>
                    ),
                    filterSearch: true,
                    onFilter: (value: any, record: Participant) => 
                        record.placeName.toLowerCase().includes(value.toLowerCase()),
                },
            ],
        },
        {
            title: 'Образовательное учреждение',
            children: [{
                    title: 'Номер школы',
                    dataIndex: 'schoolNumber',
                    key: 'schoolNumber',
                    width: 120,
                    filterSearch: true,
                    render: (schoolNumber: any) => {
                        return <div>{schoolNumber}</div>
                        },  
                    onFilter: (value: any, record: Participant) => 
                        record.schoolNumber.toLowerCase().includes(value.toLowerCase()),
                },
                {
                    title: 'Наименование школы',
                    dataIndex: 'schoolName',
                    key: 'schoolName',
                    width: 200,
                    filterSearch: true,
                    onFilter: (value: any, record: Participant) => 
                        record.schoolName.toLowerCase().includes(value.toLowerCase()),
                },
                {
                    title: 'Класс',
                    dataIndex: 'className',
                    key: 'className',
                    width: 100,
                    render: (className: string) => (
                        <Tag color="green">{className}</Tag>
                    ),
                    filterSearch: true,
                    filters: uniqueValues('className'),
                    onFilter: (value: any, record: Participant) => 
                        record.className.toLowerCase().includes(value.toLowerCase()),
                },
                {
                    title: 'Профиль',
                    dataIndex: 'classProfile',
                    key: 'classProfile',
                    width: 150,
                    render: (classProfile: string) => 
                        classProfile && classProfile !== 'Не указан' ? (
                            <Tag color="orange">{classProfile}</Tag>
                        ) : (
                            '-'
                        ),
                    filterSearch: true,
                    filters: uniqueValues('classProfile'),
                    onFilter: (value: any, record: Participant) => 
                        record.classProfile.toLowerCase().includes(value.toLowerCase()),
                },
            ],
        },
        {
            title: 'Гражданство',
            dataIndex: 'citizenship',
            key: 'citizenship',
            width: 150,
            filterSearch: true,
            filters: uniqueValues('citizenship'),
            onFilter: (value: any, record: Participant) => 
                record.citizenship.toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'Регион проживания',
            dataIndex: 'region',
            key: 'region',
            width: 150,
            filterSearch: true,
            filters: uniqueValues('region'),
            onFilter: (value: any, record: Participant) => 
                record.region.toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'Дата записи на участие',
            dataIndex: 'registrationDate',
            key: 'registrationDate',
            width: 150,
            sorter: (a: Participant, b: Participant) =>
                new Date(a.registrationDate).getTime() - new Date(b.registrationDate).getTime(),
            filterSearch: true,
            filters: uniqueValues('registrationDate'),
            onFilter: (value: any, record: Participant) => 
                record.registrationDate.toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: 'Печать',
            children: [
                {
                    title: 'Титульный лист',
                    key: 'titlePagePrint',
                    dataIndex: 'id',
                    width: 120,
                    render: (record: Participant) => <Button
                                                        icon={<DownloadOutlined/>}
                                                        type="text"
                                                        onClick={() => handleDownload('title.docx', record, olympiadId)}
                                                    >
                                                        Печать
                                                    </Button>,
                },
                {
                    title: 'Бланк ответа',
                    dataIndex: 'id',
                    key: 'answerSheetPrint',
                    width: 120,
                    render: (record: Participant) => <Button
                                                        icon={<DownloadOutlined/>}
                                                        type="text"
                                                        onClick={() => handleDownload('blank.docx', record, olympiadId)}
                                                    >
                                                        Печать
                                                    </Button>,
                    filterSearch: true,
                    filters: uniqueValues('answerSheetPrint'),
                    onFilter: (value: any, record: Participant) => 
                        record.answerSheetPrint.toLowerCase().includes(value.toLowerCase()),
                },
            ],
        },
    ];

    if (isCurator) {
        return columns.filter(column => column.key !== 'selection');
    }

    return columns;
};